import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { getTasks, createTask, updateTask, deleteTask, calculateDueDate, initializeDefaultTasks, cleanDuplicateTasks } from '../../services/tasksService'
import { defaultTasks } from '../../data/defaultTasks'
import InfoButton from '../../components/InfoButton/InfoButton'
import Loader from '../../components/Loader/Loader'
import TextareaWithCounter from '../../components/TextareaWithCounter/TextareaWithCounter'
import { showAlert, showConfirm } from '../../services/dialogService'
import './Checklist.css'

function Checklist() {
  const { user, loading: authLoading } = useAuth()
  const [tasks, setTasks] = useState([])
  const [completedTasks, setCompletedTasks] = useState([])
  const [selectedTask, setSelectedTask] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showNewTaskForm, setShowNewTaskForm] = useState(false)
  const [showCompletedTasks, setShowCompletedTasks] = useState(false)
  const [newTaskDescription, setNewTaskDescription] = useState('')
  const [newTaskTimeBefore, setNewTaskTimeBefore] = useState('')
  const [editingNotes, setEditingNotes] = useState('')
  const isInitializingRef = useRef(false)

  // Cargar tareas e inicializar si es necesario
  useEffect(() => {
    if (!user) return

    const loadTasks = async () => {
      // Prevenir múltiples inicializaciones simultáneas usando useRef
      if (isInitializingRef.current) {
        console.log('Initialization already in progress, skipping...')
        return
      }

      try {
        setLoading(true)
        
        // Cargar tareas primero
        let tasksData = await getTasks(user.id)
        
        // Si hay muchas tareas (más de 100), verificar y limpiar duplicados
        if (tasksData.length > 100) {
          console.log(`Detected ${tasksData.length} tasks, checking for duplicates...`)
          try {
            const cleanResult = await cleanDuplicateTasks(user.id)
            if (cleanResult.cleaned) {
              console.log(`Cleaned ${cleanResult.removed} duplicate tasks`)
              // Recargar tareas después de limpiar
              tasksData = await getTasks(user.id)
            }
          } catch (cleanError) {
            console.error('Error cleaning duplicates:', cleanError)
            // Continuar de todas formas
          }
        }
        
        // SOLO inicializar si NO hay tareas (verificación estricta)
        // Si hay incluso 1 tarea, NO inicializar (previene duplicaciones)
        if (tasksData.length === 0) {
          isInitializingRef.current = true
          try {
            // initializeDefaultTasks tiene verificaciones dobles internas
            const result = await initializeDefaultTasks(user.id, defaultTasks)
            
            // Recargar tareas después de la inicialización (sin importar el resultado)
            tasksData = await getTasks(user.id)
          } catch (error) {
            console.error('Error initializing default tasks:', error)
            // Recargar tareas de todas formas
            tasksData = await getTasks(user.id)
          } finally {
            isInitializingRef.current = false
          }
        }
        
        // Separar tareas completadas y pendientes de forma eficiente
        const pending = []
        const completed = []
        tasksData.forEach(task => {
          if (task.is_completed) {
            completed.push(task)
          } else {
            pending.push(task)
          }
        })
        
        setTasks(pending)
        setCompletedTasks(completed)
      } catch (error) {
        console.error('Error loading tasks:', error)
      } finally {
        // Eliminar delay innecesario para mejor rendimiento
        setLoading(false)
      }
    }

    loadTasks()
  }, [user])

  // Función helper para agrupar tareas por mes
  const groupTasksByMonth = (tasksToGroup) => {
    const grouped = {}
    
    tasksToGroup.forEach(task => {
      if (!task.due_date) {
        if (!grouped['No date']) {
          grouped['No date'] = []
        }
        grouped['No date'].push(task)
        return
      }

      const date = new Date(task.due_date)
      const monthKey = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = []
      }
      grouped[monthKey].push(task)
    })

    return grouped
  }

  // Group tasks by month - memoizado para evitar recálculos innecesarios
  const groupedTasks = useMemo(() => groupTasksByMonth(tasks), [tasks])
  
  // Group completed tasks by month - memoizado
  const groupedCompletedTasks = useMemo(() => groupTasksByMonth(completedTasks), [completedTasks])

  // Manejar crear nueva tarea - useCallback para evitar recrear función
  const handleCreateTask = useCallback(async (e) => {
    e.preventDefault()
    if (!newTaskDescription.trim()) return

    try {
      let dueDate = null
      if (newTaskTimeBefore) {
        dueDate = await calculateDueDate(user.id, newTaskTimeBefore)
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Creating task with:', {
          userId: user.id,
          title: newTaskDescription,
          description: newTaskDescription,
          due_date: dueDate
        })
      }

      const newTask = await createTask(user.id, {
        title: newTaskDescription, // Agregar title explícitamente
        description: newTaskDescription,
        due_date: dueDate
      })

      setTasks([...tasks, newTask])
      setNewTaskDescription('')
      setNewTaskTimeBefore('')
      setShowNewTaskForm(false)
      
      // Recargar todas las tareas para actualizar estadísticas
      const allTasks = await getTasks(user.id)
      setTasks(allTasks.filter(task => !task.is_completed))
      setCompletedTasks(allTasks.filter(task => task.is_completed))
    } catch (error) {
      console.error('Error creating task:', error)
      
      // Mostrar error más específico
      let errorMessage = 'Error creating task. Please try again.'
      if (error.message) {
        errorMessage = `Error: ${error.message}`
      } else if (error.code === '42501') {
        errorMessage = 'Permission denied. Please check that you have access to this wedding.'
      } else if (error.code === '23502') {
        errorMessage = 'Missing required field. Please check the task details.'
      } else if (error.details) {
        errorMessage = `Error: ${error.details}`
      }
      
      showAlert(errorMessage)
    }
  }, [user, newTaskDescription, newTaskTimeBefore, tasks, completedTasks])

  // Manejar completar tarea - useCallback para evitar recrear función
  const handleToggleComplete = useCallback(async (task) => {
    try {
      const updatedTask = await updateTask(task.id, { is_completed: !task.is_completed })
      
      if (updatedTask.is_completed) {
        // Mover a completadas
        setTasks(prev => prev.filter(t => t.id !== task.id))
        setCompletedTasks(prev => [...prev, updatedTask])
      } else {
        // Mover a pendientes
        setCompletedTasks(prev => prev.filter(t => t.id !== task.id))
        setTasks(prev => [...prev, updatedTask])
      }
      
      setSelectedTask(prev => prev?.id === task.id ? null : prev)
    } catch (error) {
      console.error('Error updating task:', error)
    }
  }, [])

  // Manejar desmarcar tarea completada - useCallback
  const handleUncompleteTask = useCallback(async (task) => {
    try {
      const updatedTask = await updateTask(task.id, { is_completed: false })
      // Mover de completadas a pendientes
      setCompletedTasks(prev => prev.filter(t => t.id !== task.id))
      setTasks(prev => [...prev, updatedTask])
      setSelectedTask(prev => prev?.id === task.id ? null : prev)
    } catch (error) {
      console.error('Error updating task:', error)
    }
  }, [])

  // Manejar actualizar notas - useCallback
  const handleUpdateNotes = useCallback(async () => {
    if (!selectedTask) return

    try {
      const updated = await updateTask(selectedTask.id, {
        user_notes: editingNotes
      })
      setSelectedTask(updated)
      setTasks(prev => prev.map(t => t.id === updated.id ? updated : t))
    } catch (error) {
      console.error('Error updating notes:', error)
    }
  }, [selectedTask, editingNotes])

  // Manejar eliminar tarea - useCallback
  const handleDeleteTask = useCallback(async () => {
    if (!selectedTask) return

    const ok = await showConfirm('Are you sure you want to delete this task?', { title: 'Delete task', confirmLabel: 'Delete', cancelLabel: 'Cancel', variant: 'danger' })
    if (!ok) return

    try {
      await deleteTask(selectedTask.id)
      // Eliminar de tasks si está pendiente
      setTasks(prev => prev.filter(t => t.id !== selectedTask.id))
      // Eliminar de completedTasks si está completada
      setCompletedTasks(prev => prev.filter(t => t.id !== selectedTask.id))
      setSelectedTask(null)
    } catch (error) {
      console.error('Error deleting task:', error)
      await showAlert('Error deleting task. Please try again.')
    }
  }, [selectedTask])

  // Calcular estadísticas - memoizado para evitar recálculos
  const stats = useMemo(() => {
    const completedCount = completedTasks.length
    const totalCount = tasks.length + completedCount
    const completionPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0
    
    let motivationalMessage = "Start checking off tasks! Every tick counts."
    if (completionPercentage >= 100) {
      motivationalMessage = "Congratulations! You've completed all tasks."
    } else if (completionPercentage >= 75) {
      motivationalMessage = "Almost done! Keep going."
    } else if (completionPercentage >= 50) {
      motivationalMessage = "You're almost there! Final push."
    } else if (completionPercentage >= 25) {
      motivationalMessage = "You're halfway there! Don't stop."
    } else if (completionPercentage > 0) {
      motivationalMessage = "You're on the right track! Keep going."
    }
    
    return { completedCount, totalCount, completionPercentage, motivationalMessage }
  }, [tasks, completedTasks])

  // Mostrar loader mientras se cargan los datos o mientras se verifica la autenticación
  if (authLoading || loading) {
    return <Loader />
  }

  return (
    <div className="checklist-page">
      <div className="checklist-header">
        <div className="checklist-header-top">
          <h1 className="checklist-title">
            Checklist
            <InfoButton
              title="Checklist"
              content={
                <div>
                  <p>
                    Your all-in-one checklist — pre-filled with smart tasks, useful tips, and space for your own chaos. 
                    Add new to-dos, set due dates, and watch your completion chart climb.
                  </p>
                  <p>
                    <strong>Features:</strong>
                  </p>
                  <ul>
                    <li>Pre-filled tasks that automatically adjust based on your wedding date</li>
                    <li>Helpful tips from The UNFILTERED COLLECTIVE for each task</li>
                    <li>Add your own custom tasks with relative time options (e.g., "6 months before")</li>
                    <li>Mark tasks as completed and track your progress</li>
                    <li>View completed tasks in a separate section</li>
                    <li>Add personal notes to each task</li>
                  </ul>
                  <p>
                    <strong>Warning:</strong> It's oddly satisfying to check off those boxes!
                  </p>
                </div>
              }
            />
          </h1>
          {completedTasks.length > 0 && (
            <button
              className="completed-tasks-header-button"
              onClick={() => setShowCompletedTasks(!showCompletedTasks)}
            >
              <span className="toggle-icon">{showCompletedTasks ? '▼' : '▶'}</span>
              Completed Tasks ({completedTasks.length})
            </button>
          )}
        </div>
        
        {/* Sección de tareas completadas - Justo debajo del header */}
        {completedTasks.length > 0 && showCompletedTasks && (
          <div className="completed-tasks-section-header">
            <div className="completed-tasks-list">
              {Object.entries(groupedCompletedTasks).map(([month, monthTasks]) => (
                <div key={month} className="tasks-month-group">
                  <h3 className="month-header completed">
                    {month === 'No date' ? 'No date' : month} ({monthTasks.length})
                  </h3>
                  {monthTasks.map(task => (
                    <div
                      key={task.id}
                      className={`task-item completed ${selectedTask?.id === task.id ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedTask(task)
                        setEditingNotes(task.user_notes || '')
                      }}
                    >
                      <div className="task-content">
                        <div className="task-description">{task.description}</div>
                        {task.due_date && (
                          <div className="task-date">
                            {new Date(task.due_date).toLocaleDateString('en-US', {
                              day: 'numeric',
                              month: 'long'
                            })}
                          </div>
                        )}
                      </div>
                      <div className="task-checkbox-wrapper">
                        <input
                          type="checkbox"
                          checked={task.is_completed}
                          onChange={() => handleUncompleteTask(task)}
                          onClick={(e) => e.stopPropagation()}
                          className="task-checkbox"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="checklist-layout">
        {/* Lista de tareas */}
        <div className="checklist-tasks">
          <button 
            className="new-task-button"
            onClick={() => setShowNewTaskForm(!showNewTaskForm)}
          >
            <span className="plus-icon">+</span>
            New task...
          </button>

          {showNewTaskForm && (
            <form className="new-task-form" onSubmit={handleCreateTask}>
              <input
                type="text"
                placeholder="Task name"
                value={newTaskDescription}
                onChange={(e) => setNewTaskDescription(e.target.value)}
                className="new-task-input"
                autoFocus
              />
              <select
                value={newTaskTimeBefore}
                onChange={(e) => setNewTaskTimeBefore(e.target.value)}
                className="new-task-select"
              >
                <option value="">Select time before wedding</option>
                <option value="12 months before">12 months before</option>
                <option value="10 months before">10 months before</option>
                <option value="9 months before">9 months before</option>
                <option value="8 months before">8 months before</option>
                <option value="6 months before">6 months before</option>
                <option value="5 months before">5 months before</option>
                <option value="3 months before">3 months before</option>
                <option value="2 months before">2 months before</option>
                <option value="1 month before">1 month before</option>
                <option value="3 weeks before">3 weeks before</option>
                <option value="2 weeks before">2 weeks before</option>
                <option value="1 week before">1 week before</option>
                <option value="3 days before">3 days before</option>
                <option value="1 day before">1 day before</option>
              </select>
              <div className="new-task-actions">
                <button type="submit" className="btn-save">Save</button>
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => {
                    setShowNewTaskForm(false)
                    setNewTaskDescription('')
                    setNewTaskTimeBefore('')
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {tasks.length === 0 && completedTasks.length === 0 ? (
            <div className="empty-state">
              <p>No tasks yet. Add your first task above!</p>
            </div>
          ) : (
            <>
              <div className="tasks-list">
                {tasks.length > 0 && Object.entries(groupedTasks).map(([month, monthTasks]) => (
                  <div key={month} className="tasks-month-group">
                  <h3 className="month-header">
                    {month === 'No date' ? 'No date' : month} ({monthTasks.length})
                  </h3>
                    {monthTasks.map(task => (
                      <div
                        key={task.id}
                        className={`task-item ${selectedTask?.id === task.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedTask(task)
                          setEditingNotes(task.user_notes || '')
                        }}
                      >
                        <div className="task-content">
                          <div className="task-description">{task.description}</div>
                          {task.due_date && (
                            <div className="task-date">
                              {new Date(task.due_date).toLocaleDateString('en-US', {
                                day: 'numeric',
                                month: 'long'
                              })}
                            </div>
                          )}
                        </div>
                        <div className="task-checkbox-wrapper">
                          <input
                            type="checkbox"
                            checked={task.is_completed}
                            onChange={() => handleToggleComplete(task)}
                            onClick={(e) => e.stopPropagation()}
                            className="task-checkbox"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Panel de detalles */}
        {selectedTask && (
          <div className="checklist-details">
            <h2 className="task-detail-title">{selectedTask.description}</h2>
            {selectedTask.due_date && (
              <div className="task-due-date">
                Due: {new Date(selectedTask.due_date).toLocaleDateString('en-US', {
                  day: 'numeric',
                  month: 'numeric',
                  year: 'numeric'
                })}
              </div>
            )}

            {selectedTask.tip_from_collective && (
              <div className="task-tip">
                {selectedTask.tip_from_collective}
              </div>
            )}

            <div className="task-notes-section">
              <label className="notes-label">Notes</label>
              <TextareaWithCounter
                className="notes-textarea"
                placeholder="Add notes..."
                value={editingNotes}
                onChange={(e) => setEditingNotes(e.target.value)}
                onBlur={handleUpdateNotes}
                maxLength={500}
                rows={3}
              />
            </div>

            <button 
              className="delete-task-button"
              onClick={handleDeleteTask}
            >
              Delete task
            </button>
          </div>
        )}

        {/* Widgets del dashboard */}
        <div className="checklist-widgets">
          <div className="widget calendar-widget">
            <h3>Calendar</h3>
            <div className="calendar-placeholder">
              Calendar view coming soon
            </div>
          </div>

          <div className="widget stats-widget">
            <h3>Progress</h3>
            <div className="progress-circle-wrapper">
              <svg className="progress-circle" width="120" height="120">
                <circle
                  className="progress-ring-background"
                  stroke="var(--color-cremita)"
                  strokeWidth="8"
                  fill="transparent"
                  r="52"
                  cx="60"
                  cy="60"
                />
                <circle
                  className="progress-ring-progress"
                  stroke="var(--color-dorado)"
                  strokeWidth="8"
                  fill="transparent"
                  r="52"
                  cx="60"
                  cy="60"
                  strokeDasharray={`${2 * Math.PI * 52}`}
                  strokeDashoffset={`${2 * Math.PI * 52 * (1 - stats.completionPercentage / 100)}`}
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <div className="progress-percentage">{stats.completionPercentage}%</div>
            </div>
            <div className="progress-stats">
              <div>Completed: {stats.completedCount}</div>
              <div>Pending: {stats.totalCount - stats.completedCount}</div>
            </div>
          </div>

          <div className="widget message-widget">
            <h3>Status</h3>
            <p className="motivational-message">
              {stats.motivationalMessage}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Checklist
